// Fix CL (2026-09-13): Proyectos → planos y cómputo métrico. "El usuario arrastra o
// selecciona un archivo desde la interfaz del ERP, el sistema lo lee en el backend, mapea
// capas y bloques, calcula, muestra un borrador para validar y lo guarda en el presupuesto"
// (CEN, 13/09). Se leen DXF (o un .zip con DXF adentro): el DWG es formato cerrado de
// Autodesk y se exporta a DXF desde el propio AutoCAD.
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, Loader2, Trash2, FileSpreadsheet, Wand2, Save, Layers, Boxes,
  ChevronDown, ChevronRight, Ruler, AlertTriangle, Plus, X,
} from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { Modal } from "../budgets/SharedComponents";
import { formatCantidad } from "../../utils/format";

const opts = { withCredentials: true };
const ESCALAS = [["", "Según el dibujo"], ["0.001", "El dibujo está en mm"], ["0.01", "El dibujo está en cm"], ["1", "El dibujo está en metros"]];
const MAPEO_VACIO = { tipo: "bloque", patron: "", capa: "", medicion: "conteo", material_codigo: "", descripcion: "", unidad: "un", factor: 1, desperdicio_pct: 0, seccion: "CÓMPUTO DE MATERIALES", orden: 0, activo: true };

// ── Alta / edición de una regla de mapeo (desde lo no mapeado, una sugerencia o el panel) ──
export function MapeoRapidoModal({ inicial, onClose, onSaved }) {
  const [f, setF] = useState({ ...MAPEO_VACIO, ...(inicial || {}) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const esEdicion = Boolean(inicial?._id);

  const guardar = async () => {
    if (!f.patron.trim() || !f.descripcion.trim()) { toast.error("Completá la capa/bloque y la descripción"); return; }
    const cuerpo = { ...f, factor: Number(f.factor) || 1, desperdicio_pct: Number(f.desperdicio_pct) || 0, orden: Number(f.orden) || 0 };
    delete cuerpo._id;
    setSaving(true);
    try {
      if (esEdicion) await axios.put(`${API}/planos/mapeos/${inicial._id}`, cuerpo, opts);
      else await axios.post(`${API}/planos/mapeos`, cuerpo, opts);
      toast.success("Regla guardada");
      onSaved?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar la regla"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={esEdicion ? "Editar regla de mapeo" : "Mapear capa o bloque a un material"}>
      <div className="p-5 space-y-3" data-testid="mapeo-rapido-modal">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Tipo</label>
            <select value={f.tipo} onChange={e => set("tipo", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="mapeo-tipo">
              <option value="bloque">Bloque (se cuenta)</option>
              <option value="capa">Capa (se miden los metros)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Medición</label>
            <select value={f.medicion} onChange={e => set("medicion", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="mapeo-medicion">
              <option value="conteo">Cantidad de unidades</option>
              <option value="longitud">Metros</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">Nombre de la capa o del bloque (admite * al final)</label>
          <input value={f.patron} onChange={e => set("patron", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" data-testid="mapeo-patron" />
        </div>
        {f.tipo === "bloque" && (
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Solo en esta capa (opcional)</label>
            <input value={f.capa} onChange={e => set("capa", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" data-testid="mapeo-capa" />
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-semibold text-gray-500 block mb-1">Descripción del material</label>
            <input value={f.descripcion} onChange={e => set("descripcion", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="mapeo-descripcion" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Código ERP</label>
            <input value={f.material_codigo} onChange={e => set("material_codigo", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" data-testid="mapeo-codigo" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Unidad</label>
            <input value={f.unidad} onChange={e => set("unidad", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="mapeo-unidad" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Factor</label>
            <input type="number" step="0.01" value={f.factor} onChange={e => set("factor", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="mapeo-factor" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Desperdicio %</label>
            <input type="number" step="1" value={f.desperdicio_pct} onChange={e => set("desperdicio_pct", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="mapeo-desperdicio" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Orden</label>
            <input type="number" value={f.orden} onChange={e => set("orden", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="mapeo-orden" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 block mb-1">Sección del cómputo</label>
          <input value={f.seccion} onChange={e => set("seccion", e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="mapeo-seccion" />
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={guardar} disabled={saving} className="flex-1 bg-[#2E6DA4] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="mapeo-guardar-btn">
            {saving ? "Guardando..." : "Guardar regla"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Fila de un plano leído, con sus capas y bloques ──
function LecturaRow({ lectura, onDelete }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl" data-testid={`plano-${lectura._id}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={() => setAbierto(o => !o)} className="text-gray-400 hover:text-gray-600" data-testid={`plano-toggle-${lectura._id}`}>
          {abierto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{lectura.filename}</p>
          <p className="text-[11px] text-gray-400">
            {lectura.nivel_label ? `${lectura.nivel_label} · ` : ""}{lectura.entidades} entidades · unidades: {lectura.unidades}
            {" · "}{(lectura.bloques || []).reduce((a, b) => a + (b.cantidad || 0), 0)} bloques
          </p>
        </div>
        <button onClick={() => onDelete(lectura)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg" data-testid={`plano-borrar-${lectura._id}`}>
          <Trash2 size={14} />
        </button>
      </div>
      {abierto && (
        <div className="px-3 pb-3 grid md:grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] font-bold text-gray-500 uppercase flex items-center gap-1 mb-1"><Boxes size={11} /> Bloques</p>
            <div className="max-h-48 overflow-y-auto text-xs">
              {(lectura.bloques || []).length === 0 ? <p className="text-gray-400">Sin bloques</p> : (lectura.bloques || []).map((b, i) => (
                <div key={i} className="flex justify-between gap-2 border-b border-gray-50 py-0.5">
                  <span className="font-mono truncate" title={`${b.nombre} · ${b.capa}`}>{b.nombre}</span>
                  <span className="font-bold text-gray-700">{b.cantidad}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] font-bold text-gray-500 uppercase flex items-center gap-1 mb-1"><Layers size={11} /> Capas con metros</p>
            <div className="max-h-48 overflow-y-auto text-xs">
              {(lectura.capas || []).filter(c => (c.longitud_m || 0) > 0).length === 0 ? <p className="text-gray-400">Sin líneas medibles</p>
                : (lectura.capas || []).filter(c => (c.longitud_m || 0) > 0).map((c, i) => (
                  <div key={i} className="flex justify-between gap-2 border-b border-gray-50 py-0.5">
                    <span className="font-mono truncate" title={c.nombre}>{c.nombre}</span>
                    <span className="font-bold text-gray-700">{formatCantidad(c.longitud_m)} m</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PlanosComputoModal({ open, onClose, task, onSaved }) {
  const [lecturas, setLecturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [nivel, setNivel] = useState("");
  const [escala, setEscala] = useState("");
  const [computo, setComputo] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [modo, setModo] = useState("agregar");
  const [sugerencias, setSugerencias] = useState(null);
  const [mapeoRapido, setMapeoRapido] = useState(null);

  const cargar = useCallback(async () => {
    if (!task?._id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/planos/lecturas?computo_task_id=${task._id}`, opts);
      setLecturas(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar los planos"); }
    finally { setLoading(false); }
  }, [task?._id]);

  useEffect(() => { if (open) { cargar(); setComputo(null); setSugerencias(null); } }, [open, cargar]);

  const subir = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !task?._id) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("computo_task_id", task._id);
    fd.append("nivel_label", nivel);
    fd.append("escala_forzada", escala);
    setSubiendo(true);
    try {
      const { data } = await axios.post(`${API}/planos/lecturas`, fd, { ...opts, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.count} plano(s) leído(s)`);
      setComputo(null);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al leer el plano", { duration: 8000 }); }
    finally { setSubiendo(false); }
  };

  const borrarLectura = async (lectura) => {
    try {
      await axios.delete(`${API}/planos/lecturas/${lectura._id}`, opts);
      setComputo(null);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al quitar el plano"); }
  };

  const generar = async () => {
    setGenerando(true);
    try {
      const { data } = await axios.post(`${API}/planos/computo/${task._id}`, {}, opts);
      setComputo(data);
      if (!(data.items || []).length) toast.warning("Ninguna capa ni bloque coincide con el mapeo cargado");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar el cómputo"); }
    finally { setGenerando(false); }
  };

  const pedirSugerencias = async () => {
    try {
      const { data } = await axios.post(`${API}/planos/mapeos/sugerir?computo_task_id=${task._id}`, {}, opts);
      setSugerencias(data.sugerencias || []);
      if (!(data.sugerencias || []).length) toast.success("No quedan capas ni bloques sin mapear");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al sugerir el mapeo"); }
  };

  const setItem = (idx, patch) => setComputo(prev => ({
    ...prev, items: prev.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
  }));

  const cambiarDesperdicio = (idx, valor) => {
    const pct = Number(valor) || 0;
    setComputo(prev => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx
        ? { ...it, desperdicio_pct: pct, cantidad: Math.round((Number(it.cantidad_calculada) || 0) * (1 + pct / 100) * 100) / 100 }
        : it)),
    }));
  };

  const guardar = async () => {
    if (!computo?.items?.length) return;
    setGuardando(true);
    try {
      const { data } = await axios.post(`${API}/planos/computo/${task._id}/guardar`, { items: computo.items, modo }, opts);
      toast.success(`${data.items_guardados} ítems guardados en el presupuesto`);
      onSaved?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar el cómputo"); }
    finally { setGuardando(false); }
  };

  const bajarExcel = async () => {
    if (!computo?.items?.length) return;
    try {
      const res = await axios.post(`${API}/planos/computo/${task._id}/excel`, { items: computo.items, modo }, { ...opts, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `computo_${task.budget_number || "planos"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Error al bajar el Excel"); }
  };

  if (!open || !task) return null;
  const items = computo?.items || [];
  const noMapeado = computo?.no_mapeado || [];

  return (
    <>
      <Modal open={open} onClose={onClose} title={`Planos y cómputo — ${task.budget_number || ""}`} fullscreen>
        <div className="p-5 space-y-5" data-testid="planos-computo-modal">
          {/* 1. Carga de planos */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-semibold text-gray-500 block mb-1">Nivel / sector del plano (opcional)</label>
                <input value={nivel} onChange={e => setNivel(e.target.value)} placeholder="Ej.: Planta Baja"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="plano-nivel" />
              </div>
              <div className="min-w-[190px]">
                <label className="text-xs font-semibold text-gray-500 block mb-1">Escala</label>
                <select value={escala} onChange={e => setEscala(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="plano-escala">
                  {ESCALAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer ${subiendo ? "bg-gray-200 text-gray-500" : "bg-[#2E6DA4] text-white hover:bg-[#25597F]"}`} data-testid="plano-upload-label">
                {subiendo ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                {subiendo ? "Leyendo el plano..." : "Cargar plano"}
                <input type="file" accept=".dxf,.zip,.dwg" className="hidden" onChange={subir} disabled={subiendo} data-testid="plano-file-input" />
              </label>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              Se leen archivos <strong>DXF</strong> o un <strong>.zip</strong> con varios DXF adentro (hasta 60 MB). Desde AutoCAD:
              Archivo → Guardar como → AutoCAD DXF.
            </p>
          </div>

          {/* 2. Planos leídos */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Planos leídos <span className="font-normal text-gray-400">({lecturas.length})</span></p>
            {loading ? <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
              : lecturas.length === 0 ? <p className="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-xl">Todavía no hay planos cargados en esta tarea</p>
                : <div className="space-y-2">{lecturas.map(l => <LecturaRow key={l._id} lectura={l} onDelete={borrarLectura} />)}</div>}
          </div>

          {/* 3. Acciones */}
          <div className="flex flex-wrap gap-2">
            <button onClick={generar} disabled={generando || lecturas.length === 0}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="generar-computo-btn">
              {generando ? <Loader2 size={15} className="animate-spin" /> : <Ruler size={15} />} Generar cómputo
            </button>
            <button onClick={pedirSugerencias} disabled={lecturas.length === 0}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-violet-200 text-violet-700 hover:bg-violet-50 text-sm font-semibold disabled:opacity-50" data-testid="sugerir-mapeo-btn">
              <Wand2 size={15} /> Sugerir mapeo
            </button>
            <button onClick={() => setMapeoRapido({ ...MAPEO_VACIO })}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-semibold" data-testid="nueva-regla-btn">
              <Plus size={15} /> Nueva regla de mapeo
            </button>
          </div>

          {/* 4. Sugerencias */}
          {sugerencias && sugerencias.length > 0 && (
            <div className="bg-violet-50/50 border border-violet-200 rounded-2xl p-4" data-testid="sugerencias-mapeo">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-violet-800">Capas y bloques sin mapear ({sugerencias.length})</p>
                <button onClick={() => setSugerencias(null)} className="text-violet-500 hover:text-violet-700"><X size={15} /></button>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {sugerencias.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 text-xs">
                    <span className={`px-1.5 py-0.5 rounded font-bold ${s.tipo === "capa" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>{s.tipo}</span>
                    <span className="font-mono flex-1 truncate" title={s.patron}>{s.patron}</span>
                    <span className="text-gray-500">{formatCantidad(s.cantidad_detectada)} {s.medicion === "longitud" ? "m" : "un"}</span>
                    <button onClick={() => setMapeoRapido(s)} className="text-[#2E6DA4] font-semibold hover:underline" data-testid={`sugerencia-mapear-${i}`}>Mapear</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 5. Cómputo generado */}
          {computo && (
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="computo-preview">
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-bold text-gray-800">Cómputo generado <span className="font-normal text-gray-400">({items.filter(i => !i.is_group).length} ítems)</span></p>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={modo} onChange={e => setModo(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs" data-testid="computo-modo">
                    <option value="agregar">Agregar al presupuesto</option>
                    <option value="reemplazar">Reemplazar el cómputo del presupuesto</option>
                  </select>
                  <button onClick={bajarExcel} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid="computo-excel-btn">
                    <FileSpreadsheet size={13} /> Excel
                  </button>
                  <button onClick={guardar} disabled={guardando || items.length === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E6DA4] hover:bg-[#25597F] text-white text-xs font-semibold disabled:opacity-60" data-testid="computo-guardar-btn">
                    {guardando ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar en el presupuesto
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-400 uppercase">
                    <tr>
                      <th className="text-left px-3 py-2 w-16">Ítem</th>
                      <th className="text-left px-3 py-2 w-24">Código</th>
                      <th className="text-left px-3 py-2">Descripción</th>
                      <th className="text-left px-3 py-2 w-16">Unidad</th>
                      <th className="text-right px-3 py-2 w-28">Cant. calculada</th>
                      <th className="text-right px-3 py-2 w-24">Desperdicio %</th>
                      <th className="text-right px-3 py-2 w-28">Cant. total</th>
                      <th className="px-2 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody data-testid="computo-items">
                    {items.map((it, idx) => it.is_group ? (
                      <tr key={idx} className="bg-[#EBF3FB] font-bold text-[#1A4B7A]">
                        <td className="px-3 py-2">{it.numero}</td>
                        <td colSpan={7} className="px-3 py-2">{it.descripcion}</td>
                      </tr>
                    ) : (
                      <tr key={idx} className="border-b border-gray-50" data-testid={`computo-item-${idx}`}>
                        <td className="px-3 py-1.5 font-mono text-gray-400">{it.numero}</td>
                        <td className="px-3 py-1.5 font-mono text-gray-500">{it.material_codigo || "—"}</td>
                        <td className="px-1 py-1.5">
                          <input value={it.descripcion} onChange={e => setItem(idx, { descripcion: e.target.value })}
                            className="w-full border border-transparent hover:border-gray-200 focus:border-[#2E6DA4] rounded px-2 py-1" data-testid={`computo-desc-${idx}`} />
                        </td>
                        <td className="px-1 py-1.5">
                          <input value={it.unidad} onChange={e => setItem(idx, { unidad: e.target.value })}
                            className="w-14 border border-transparent hover:border-gray-200 focus:border-[#2E6DA4] rounded px-2 py-1" />
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-500" title={(it.detalle || []).map(d => `${d.plano}: ${d.origen} = ${d.medido}`).join("\n")}>
                          {formatCantidad(it.cantidad_calculada)}
                        </td>
                        <td className="px-1 py-1.5 text-right">
                          <input type="number" step="1" value={it.desperdicio_pct} onChange={e => cambiarDesperdicio(idx, e.target.value)}
                            className="w-16 text-right border border-gray-200 rounded px-2 py-1" data-testid={`computo-desperdicio-${idx}`} />
                        </td>
                        <td className="px-1 py-1.5 text-right">
                          <input type="number" step="0.01" value={it.cantidad} onChange={e => setItem(idx, { cantidad: Number(e.target.value) || 0 })}
                            className="w-24 text-right font-bold border border-gray-200 rounded px-2 py-1" data-testid={`computo-cantidad-${idx}`} />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <button onClick={() => setComputo(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))}
                            className="text-red-400 hover:text-red-600" data-testid={`computo-borrar-${idx}`}><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr><td colSpan={8} className="text-center py-6 text-gray-400">Ninguna capa ni bloque coincide con el mapeo cargado</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {noMapeado.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-100 bg-amber-50/40" data-testid="computo-no-mapeado">
                  <p className="text-xs font-bold text-amber-700 flex items-center gap-1 mb-1">
                    <AlertTriangle size={12} /> Quedó sin mapear ({noMapeado.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {noMapeado.slice(0, 30).map((n, i) => (
                      <button key={i} onClick={() => setMapeoRapido({ ...MAPEO_VACIO, tipo: n.tipo, patron: n.nombre, descripcion: n.nombre, medicion: n.tipo === "capa" ? "longitud" : "conteo", unidad: n.tipo === "capa" ? "m" : "un" })}
                        className="text-[11px] bg-white border border-amber-200 rounded-full px-2 py-0.5 hover:bg-amber-100" data-testid={`no-mapeado-${i}`}>
                        {n.nombre} · {formatCantidad(n.cantidad)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
      {mapeoRapido && (
        <MapeoRapidoModal inicial={mapeoRapido} onClose={() => setMapeoRapido(null)} onSaved={() => { setSugerencias(null); generar(); }} />
      )}
    </>
  );
}
